/* eslint-disable lodash/prefer-lodash-method */
import { createMuiTheme } from '@material-ui/core/styles';
import mediaQuery from 'css-mediaquery';

import { MuiAccordion, MuiAccordionSummary, MuiAccordionActions } from 'shared/components/accordion';
import { isBrowser } from 'browser-or-node';

import { InterfaceVariants } from 'src/constants/interface-variants';
import { getSsrViewportWidth } from 'src/utils/device/detect-device-type';

import { normalAnimations, normalTransitions } from './shared';

export const getMuiTheme = ({ colors, customized, uiVariant, deviceType = 'desktop' }) => {
  const ssrMatchMedia = (query) => ({
    matches: mediaQuery.match(query, {
      // Use actual browser width when available. During SSR we don't know the real
      // viewport, so seed it from the User-Agent-derived device type so the first
      // paint matches the visitor's device (instead of always assuming mobile).
      width: isBrowser ? window.innerWidth : getSsrViewportWidth(deviceType),
    }),
  });

  const muiTabIndicatorColor =
    uiVariant === InterfaceVariants.dutchieMain ? colors.primaryGreen : customized.colors.buttonsLinks;

  return createMuiTheme({
    palette: { ...colors, primary: { main: colors.primaryBlue }, customized },
    overrides: {
      MuiAccordion,
      MuiAccordionSummary,
      MuiAccordionActions,
      MuiMenu: {
        paper: {
          width: '187px',
        },
        list: {
          padding: 0,
          '& li:first-child': {
            height: `42px`,
            alignItems: `flex-end`,
          },
          '& li:last-child': {
            height: `42px`,
            alignItems: `flex-start`,
          },
        },
      },
      MuiMenuItem: {
        root: {
          fontSize: '14px',
          height: '36px',
          paddingLeft: '29px',
          color: colors.grey[25],
          background: colors.white,
          '&:hover': {
            background: `${colors.primaryGrey} !important`,
          },
          '&$selected': {
            fontWeight: 'bold',
            '&:hover': {
              background: `${colors.primaryGrey} !important`,
            },
          },
        },
      },
      MuiTabs: {
        root: {
          minHeight: 32,
          borderBottom: `1px solid ${colors.blueGrey[90]}`,
        },
        indicator: {
          backgroundColor: muiTabIndicatorColor,
        },
      },
      MuiTab: {
        root: {
          lineHeight: '18px',
          fontSize: '16px',
          fontWeight: 'bold',
          textTransform: 'none',
          minHeight: 30,
          padding: '6px 17px',
          margin: '0px 18px',
          minWidth: 'auto !important',
          color: colors.grey[30],

          '&$textColorInherit': {
            opacity: 1,
          },

          '&$selected': {
            color: muiTabIndicatorColor,
          },
        },
      },
      MuiPopover: {
        paper: {
          backgroundColor: colors.white,
          borderRadius: '8px',
          boxShadow: '0 5px 15px 0 rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      },
      MuiListItem: {
        root: {
          '&$selected': { backgroundColor: 'initial', fontWeight: 'bold' },
        },
      },
      MuiInputBase: {
        root: {
          fontFamily: customized.fonts.secondary,
          borderRadius: customized.radius.sm,
        },
      },
      MuiFilledInput: {
        root: {
          borderRadius: customized.radius.sm,
          borderTopLeftRadius: customized.radius.sm,
          borderTopRightRadius: customized.radius.sm,
        },
      },
      MuiFormLabel: {
        root: {
          fontFamily: customized.fonts.secondary,
        },
      },
    },
    props: {
      MuiUseMediaQuery: { ssrMatchMedia },
      MuiButtonBase: {
        disableRipple: true, // No ripple in the whole application
      },
      MuiSelect: {
        variant: 'outlined',
        autoWidth: true,
      },
      MuiOutlinedInput: { notched: false },
    },
    animations: normalAnimations,
    transitions: normalTransitions,
    customized,
  });
};
